/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { AccessTokenClaims, JWT, OAuth2Client } from "@vmw/csp-oauth2";
import type { IdTokenClaims, JsonWebToken } from "@vmw/csp-oauth2";
import { OAuth2TokenSubject } from "@vmw/ngx-csp-oauth2";
import { take, tap } from "rxjs/operators";
import { AUTH_KEYS } from "@app/services";

export class User {
    constructor(public email: string,
                public firstName: string,
                public lastName: string,
                public orgId: string,
                public roles: string[]) {
    }
}

/**
 * Get user information from OAuth2Client
 *
 * @param oAuth2Client
 */
export function getUser(oAuth2Client: OAuth2Client): User {
    const idToken: string = oAuth2Client.idToken();
    const accessToken: string = oAuth2Client.accessToken();
    const idJwt: JsonWebToken = JWT.nullSafeParse(idToken);
    const accessJwt: JsonWebToken = JWT.nullSafeParse(accessToken);

    if (accessJwt) {
        const idJwClaims: IdTokenClaims = idJwt?.claims as IdTokenClaims;
        const accessJwtClaims: AccessTokenClaims = accessJwt.claims as AccessTokenClaims;

        const user: User = new User(idJwClaims?.email || accessJwtClaims.acct || "",
                                    idJwClaims?.given_name || accessJwtClaims.username || "",
                                    idJwClaims?.family_name || "",
                                    accessJwtClaims.context_name,
                                    accessJwtClaims.perms);
        if (!idJwt) {
            console.log("User: loaded without id_token");
        }
        return user;
    } else {
        console.log("User: no user");
        return new User("", "", "", "", []);
    }
}

export function getUserName(oAuth2Client: OAuth2Client): string {
    const user = getUser(oAuth2Client);
    return user.firstName + " " + user.lastName;
}

const MAX_USER_DATA_LOADING_RETRIES = 3;

export function loadUserData(oauth2TokenSubject: OAuth2TokenSubject,
                             oAuth2Client: OAuth2Client): () => Promise<any> {
    return () => new Promise((resolve, reject) => {
        oauth2TokenSubject.pipe(
            take(MAX_USER_DATA_LOADING_RETRIES),
            tap((token) => {
                    if (token && token.access_token) {
                        const user: User = getUser(oAuth2Client);
                        window.sessionStorage.setItem(AUTH_KEYS.ORG_ID, user.orgId);
                        console.log("Loaded user data for org " + user.orgId);
                    } else {
                        console.log("Could not load user data. Token not received yet, retrying...");
                    }
                },
                error => {
                    console.log("Could not load user data", error);
                })
        ).subscribe(
            {
                next: (unused) => resolve(true),
                error: (error) => reject(false)
            });
    });
}
